// Los colores y las ocasiones que creaste vos.
//
// POR QUÉ HACÍA FALTA UNA PANTALLA
//
// Crear un color o una ocasión se hace donde se necesita: parado en una
// prenda, editándola. Eso está bien — es el momento en que uno descubre
// que falta "Fucsia".
//
// Borrar es al revés. No se borra en el momento de usar algo: se borra
// cuando uno mira la lista entera y ve que tiene tres ocasiones que
// inventó en un mal día. Es una tarea de mantenimiento, y va donde
// están las otras: en el perfil.
//
// LO QUE ESTA PANTALLA NO HACE, A PROPÓSITO
//
// No deja EDITAR. Cambiarle el nivel a una ocasión, o la familia a un
// color, le cambiaría el significado a todas las prendas que ya la
// tienen marcada, sin que se vea qué prendas son. Borrar y volver a
// crear obliga a pasar por el aviso de "está en uso en N prendas", que
// es exactamente la información que hace falta para decidir.
//
// LOS VETOS TAMBIÉN VIVEN ACÁ (AL-9, 2026-09-03)
//
// "No combina" se marca donde se descubre: mirando una sugerencia en
// "Qué me pongo". Deshacerlo es lo contrario, igual que con los colores:
// uno no se arrepiente en el momento, se arrepiente cuando mira la
// lista entera. Así que va donde están las otras listas.
//
// Y era una puerta sin picaporte del lado de adentro: `listarVetos` y
// `borrarVeto` existían en `src/api.js` desde siempre y NINGUNA pantalla
// los importaba. El usuario marcaba un par como que no va, se
// arrepentía, y no tenía forma de volver atrás sin entrar a la base.
//
// A diferencia de colores y ocasiones, quitar un veto NO PIDE
// CONFIRMACIÓN: no destruye nada. Esas dos prendas vuelven a poder
// aparecer juntas, y si te arrepentís las volvés a marcar en dos
// clics desde la sugerencia. Un cartel de "¿estás seguro?" para algo
// reversible le cobra un clic a todo el mundo para no proteger a nadie.
//
// EL BORRADO NO ES EN CASCADA
//
// El servidor responde 409 si la lista está en uso y hay que confirmar.
// Sin eso, borrar "Casual propio" le sacaría la categoría a veinte
// prendas de un clic, y las prendas sin categoría se saltean la regla
// de formalidad — un borrado descuidado degradaría las sugerencias en
// silencio.

import { useCallback, useEffect, useState } from "react";
import * as api from "./api";
import { invalidarOpciones } from "./opciones";
import ArmarListas from "./ArmarListas";
import NuevaOcasion from "./NuevaOcasion";
import NuevoColor from "./NuevoColor";
import { t, tCat, tError, useIdioma, numero } from "./i18n";
import { useFocoAtrapado } from "./foco";

/* ⚠️  `alCambiar` SE USA PARA RECARGAR LO QUE ESTÁ TAPADO POR EL MODAL,
 *     y faltaba desde antes de esta pantalla (2026-09-03).
 *
 * Borrar un color, una ocasión o un veto CAMBIA LAS SUGERENCIAS. Pero
 * `Perfil` montaba `<MisListas />` sin pasarle nada, así que ningún
 * borrado marcaba el perfil como tocado y al cerrar el modal la
 * pantalla de atrás seguía con los datos viejos.
 *
 * En los vetos se nota mucho más que en los colores: uno quita un veto
 * PARA VOLVER A VER esa combinación. Si al cerrar sigue sin aparecer, la
 * conclusión razonable del usuario es que el botón no funcionó.
 *
 * Se avisa y no se recarga acá: la recarga la hace `cerrarPerfil`,
 * cuando la pantalla vuelve a estar a la vista. Recargar con el modal
 * abierto es lo que fabricaba impresiones fantasma (ver App.js). */
export default function MisListas({ alCambiar }) {
  /* Se repinta cuando llega el idioma. Sin esto el componente depende
   * de que su padre se repinte por otra razón — hoy pasa, y por eso el
   * bug del 2026-08-25 se veía en unas pantallas y no en otras. */
  useIdioma();
  const [colores, setColores] = useState(null);
  const [ocasiones, setOcasiones] = useState(null);
  const [sembrado, setSembrado] = useState(true);
  const [paquetes, setPaquetes] = useState([]);
  const [problemas, setProblemas] = useState([]);
  const [vetos, setVetos] = useState([]);
  /* La lista de vetos se abre en su propia ventana y NO en línea
   * (pedido del dueño, 2026-09-03): son una fila por par y con veinte
   * pares el perfil se vuelve un scroll interminable, justo en la
   * pantalla donde uno viene a cambiar UNA cosa. Acá abajo queda un
   * botón con el número; la lista vive aparte. */
  const [vetosAbiertos, setVetosAbiertos] = useState(false);
  const [error, setError] = useState("");

  /* ⚠️  LOS BOTONES DE CREAR VIVEN ACÁ DESDE EL 2026-09-05, Y ANTES NO.
   *
   * Se creaban SOLO desde el editor de una prenda. "Mis listas" lo
   * decía... únicamente cuando la lista estaba vacía. O sea que la
   * única pista desaparecía exactamente cuando el usuario ya tenía
   * ocasiones y venía a esta pantalla a agregar otra.
   *
   * Reportado por el dueño: *"no estoy encontrando dónde agregar
   * ocasiones, o no tiene esa posibilidad?"*. La tenía. La pantalla que
   * lleva el nombre no la ofrecía.
   *
   * Se agregan ACÁ ADEMÁS de en el editor, no en lugar de: crear una
   * ocasión parado en la prenda que la necesita sigue siendo el camino
   * más corto, y ese flujo también la marca en la prenda. */
  const [creando, setCreando] = useState(null); // "color" | "ocasion" | null
  const [niveles, setNiveles] = useState({});
  const [familias, setFamilias] = useState([]);
  const [muestras, setMuestras] = useState([]);

  const traer = useCallback(async () => {
    try {
      setError("");
      const [c, o, v] = await Promise.all([
        api.listarColores(),
        api.listarOcasiones(),
        /* Los vetos NO pueden tumbar la pantalla: son la tercera
         * sección y las otras dos sirven sin ellos. Se degrada y se
         * DICE — el error entra en `problemas`, que se dibuja arriba
         * de todo. Tragarlo en silencio dejaría a alguien mirando una
         * lista de vetos vacía que no es la verdad. */
        api.listarVetos().catch((e) => ({ vetos: [], error: tError(e) })),
      ]);
      setColores(c.propios || []);
      setOcasiones(o.propias || []);
      setVetos(v.vetos || []);
      // El servidor ahora avisa si NO PUDO leer, en vez de mandar una
      // lista vacía y callarse. La distinción importa: una lista vacía
      // de verdad es "todavía no creaste ninguna"; una lista vacía por
      // error es "falta correr un SQL", y son cosas opuestas.
      setProblemas([c.error, o.error, v.error].filter(Boolean));
      // Arranca en `true` a propósito: mientras carga no se muestra el
      // bloque de "armá tu lista", que apareciendo y desapareciendo en
      // cada apertura del perfil sería un parpadeo molesto para quien
      // ya la armó.
      setSembrado(!!o.sembrado);
      setPaquetes(o.paquetes || []);
      // Lo que los creadores necesitan. Sale de las MISMAS respuestas
      // que ya se están pidiendo: sin viajes de más.
      setNiveles(o.niveles || {});
      setFamilias(c.familias || []);
      setMuestras(c.muestras || []);
    } catch (e) {
      setError(tError(e));
    }
  }, []);

  useEffect(() => {
    traer();
  }, [traer]);

  if (error) return <div className="mensaje-error texto-mini" role="alert">{error}</div>;
  if (!colores || !ocasiones)
    return <p className="tenue texto-mini">{t("listas.cargando", "Cargando tus listas...")}</p>;

  /* ⚠️  SE DEFINE ACÁ, ARRIBA DEL `return` DEL CASO VACÍO.
   *
   * La primera versión lo dejaba más abajo y lo usaba en los dos
   * returns: `const` no se iza, así que el caso vacío lo leía en zona
   * muerta temporal y la pantalla se caía entera. Es exactamente el bug
   * de la ficha en blanco del 4/9 (DOCUMENTACION §32), en otro archivo,
   * once días después. Lo agarró `test_orden_declaraciones.js` §2a. */
  const crear = (
    <>
      <div className="listas-crear">
        <button
          type="button"
          className="chip chip-nuevo"
          onClick={() => setCreando("ocasion")}
        >
          {t("listas.botonOcasionNueva", "+ Ocasión nueva")}
        </button>
        <button
          type="button"
          className="chip chip-nuevo"
          onClick={() => setCreando("color")}
        >
          {t("listas.botonColorNuevo", "+ Color nuevo")}
        </button>
      </div>

      {creando === "ocasion" && (
        <NuevaOcasion
          niveles={niveles}
          onCreada={() => {
            setCreando(null);
            // Se recarga la lista entera y se avisa hacia arriba: la
            // ocasión nueva cambia los filtros de las otras pantallas.
            traer();
            alCambiar?.();
          }}
          onCerrar={() => setCreando(null)}
        />
      )}

      {creando === "color" && (
        <NuevoColor
          familias={familias}
          muestras={muestras}
          onCreado={() => {
            setCreando(null);
            traer();
            alCambiar?.();
          }}
          onCerrar={() => setCreando(null)}
        />
      )}
    </>
  );


  // Va ARRIBA de todo y antes que cualquier otra cosa: si falta la
  // tabla, todo lo que se muestre debajo es mentira.
  const aviso = problemas.length > 0 && (
    <div className="mensaje-error texto-mini" role="alert">
      {problemas.map((p) => (
        <p key={p} style={{ margin: 0 }}>
          {p}
        </p>
      ))}
    </div>
  );

  // El bloque de armado va PRIMERO y solo si no sembró. Para un usuario
  // nuevo es lo único que hay para hacer acá; para uno que ya la armó no
  // existe.
  const armador = !sembrado && problemas.length === 0 && paquetes.length > 0 && (
    <div className="mis-listas-grupo">
      <p className="filtro-titulo">{t("listas.armaTuLista", "Arma tu lista de ocasiones")}</p>
      <ArmarListas paquetes={paquetes} onListo={traer} />
    </div>
  );

  /* ⚠️  LOS VETOS ENTRAN EN ESTA CUENTA. Sin eso, alguien con cero
   *     colores propios, cero ocasiones propias y tres vetos caía en el
   *     texto de "todavía no creaste nada" — y sus tres vetos quedaban
   *     invisibles justo en la pantalla que existe para verlos. */
  if (colores.length === 0 && ocasiones.length === 0 && vetos.length === 0) {
    return (
      <div className="mis-listas">
        {aviso}
        {armador}
        {crear}
        <p className="tenue texto-mini" style={{ margin: 0 }}>
          {t("listas.vacio", "Todavía no creaste ningún color ni ocasión propia. Se crean editando una prenda, con los botones")}{" "}
          <strong>{t("listas.botonColorNuevo", "+ Color nuevo")}</strong>{" "}
          {t("listas.y", "y")}{" "}
          <strong>{t("listas.botonOcasionNueva", "+ Ocasión nueva")}</strong>.
        </p>
      </div>
    );
  }

  return (
    <div className="mis-listas">
      {aviso}
      {armador}
      {crear}

      {ocasiones.length > 0 && (
        <Grupo
          titulo={
            sembrado
              ? t("listas.tusOcasiones", "Tus ocasiones")
              : t("listas.ocasionesCreadas", "Ocasiones que creaste")
          }
          items={ocasiones.map((o) => ({
            clave: o.clave,
            /* ⚠️  `grupo` existe para que el nombre pase por `tCat`.
             * Las ocasiones SEMBRADAS se guardan en español —son la
             * clave del catálogo— y sin esto se dibujaban en español en
             * medio de una pantalla en portugués. Una ocasión que creó
             * el usuario no está en el catálogo y `tCat` la devuelve tal
             * cual, que es lo correcto: es su texto. */
            grupo: "ocasion",
            nombre: o.nombre,
            detalle: t("listas.formalidad", "formalidad {n}", { n: o.nivel }),
          }))}
          borrar={api.borrarOcasion}
          onCambio={traer}
          alCambiar={alCambiar}
          queEs={t("listas.ocasion", "ocasión")}
        />
      )}

      {colores.length > 0 && (
        <Grupo
          titulo={t("listas.coloresCreados", "Colores que creaste")}
          items={colores.map((c) => ({
            clave: c.clave,
            grupo: "color",
            nombre: c.nombre,
            detalle: `${c.familia}${
              c.es_neutro ? ` · ${t("listas.neutro", "neutro")}` : ""
            }${c.hex ? "" : ` · ${t("listas.sinMuestra", "sin muestra")}`}`,
            hex: c.hex,
          }))}
          borrar={api.borrarColor}
          onCambio={traer}
          alCambiar={alCambiar}
          queEs={t("listas.color", "color")}
        />
      )}

      {vetos.length > 0 && (
        <div className="mis-listas-grupo">
          {/* Se reusa el MISMO texto que titula la ventana: es la misma
              cosa, y una etiqueta distinta para el botón y para lo que
              abre obliga a leer dos veces para entender que son una.
              El número va suelto al lado, sin plural, igual que los
              chips de filtro del armario — así no hace falta una forma
              plural nueva en doce idiomas para mostrar un entero. */}
          <button
            type="button"
            className="boton-secundario mis-listas-abrir"
            onClick={() => setVetosAbiertos(true)}
          >
            {t("listas.vetosTitulo", "Combinaciones que marcaste como que no van")}
            <span className="chip-conteo"> {numero(vetos.length)}</span>
          </button>
        </div>
      )}

      {/* ⚠️  SE DESMONTA SOLA AL QUEDAR VACÍA, y es deliberado: al quitar
          el último veto no queda nada que mirar, así que la ventana se
          cierra y volvés al perfil. La alternativa era un texto de
          "ya no tenés ninguna", que en una ventana que existe SOLO para
          esa lista es una pantalla en blanco con una excusa. */}
      {vetosAbiertos && vetos.length > 0 && (
        <VentanaVetos
          vetos={vetos}
          onCambio={traer}
          alCambiar={alCambiar}
          onCerrar={() => setVetosAbiertos(false)}
        />
      )}
    </div>
  );
}

function Grupo({ titulo, items, borrar, onCambio, alCambiar, queEs }) {
  // Qué está esperando confirmación, y con qué mensaje. Es por item y
  // no del grupo: con un estado compartido, pedir confirmación para uno
  // marcaría a los cinco.
  const [confirmando, setConfirmando] = useState(null);
  const [yendo, setYendo] = useState(null);
  const [error, setError] = useState("");

  async function intentar(clave, forzar) {
    setYendo(clave);
    setError("");
    try {
      await borrar(clave, forzar);
      // El caché de opciones tiene la lista vieja: sin tirarlo, el
      // color borrado sigue apareciendo en los filtros.
      invalidarOpciones();
      setConfirmando(null);
      await onCambio();
      // La pantalla de atrás tiene que enterarse. Ver el bloque de
      // `alCambiar` en la cabecera del componente.
      alCambiar?.();
    } catch (e) {
      // 409 = está en uso. No es un error: es la pregunta que hay que
      // hacer antes de borrar. El servidor manda cuántas prendas en el
      // mensaje. Se mira el STATUS y no el texto: un regex sobre el
      // mensaje se rompería el día que alguien mejore la redacción.
      if (e.status === 409) {
        setConfirmando({ clave, mensaje: tError(e) });
      } else {
        setError(tError(e));
      }
    } finally {
      setYendo(null);
    }
  }

  return (
    <div className="mis-listas-grupo">
      <p className="filtro-titulo">{titulo}</p>
      {error && <div className="mensaje-error texto-mini" role="alert">{error}</div>}

      {items.map((it) => (
        <div key={it.clave} className="mis-listas-fila">
          <div className="mis-listas-nombre">
            {/* La muestra al lado del nombre: es la única forma de ver
                si el color que elegiste es el que pensabas. Un hex en
                texto no se lee. */}
            {it.hex && (
              <span className="muestra-mini" style={{ background: it.hex }} />
            )}
            <span>
              <strong>{tCat(it.grupo, it.nombre)}</strong>{" "}
              <span className="tenue texto-mini">{it.detalle}</span>
            </span>
          </div>

          {confirmando?.clave === it.clave ? (
            <div className="mis-listas-confirma">
              <span className="texto-mini">{confirmando.mensaje}</span>
              <button
                className="boton-texto peligro"
                onClick={() => intentar(it.clave, true)}
                disabled={yendo === it.clave}
              >
                {t("listas.borrarIgual", "Borrar igual")}
              </button>
              <button
                className="boton-texto"
                onClick={() => setConfirmando(null)}
              >
                {t("listas.cancelar", "Cancelar")}
              </button>
            </div>
          ) : (
            <button
              className="boton-texto"
              onClick={() => intentar(it.clave, false)}
              disabled={yendo === it.clave}
              title={t("listas.borrarTitulo", "Borrar esta {que}", { que: queEs })}
            >
              {yendo === it.clave ? "..." : t("listas.borrar", "Borrar")}
            </button>
          )}
        </div>
      ))}

      {/* Se dice qué pasa con las prendas, porque es lo que uno se
          pregunta justo antes de tocar "Borrar igual".

          Y se dice EXACTO. El primer texto decía "las prendas la
          conservan", y es verdad a medias: el validador del servidor
          descarta los valores que no están en ninguna lista, así que el
          huérfano sobrevive hasta que guardes esa prenda. Una promesa
          que se cumple hasta que el usuario hace algo normal es peor
          que no prometer nada. */}
      <p className="texto-mini tenue" style={{ margin: "4px 0 0" }}>
        {t("listas.notaBorrado", "Las prendas que ya la tenían marcada la siguen mostrando con ⚠, pero la pierden la próxima vez que guardes esa prenda.")}
      </p>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   LOS PARES QUE MARCASTE COMO QUE NO VAN

   Un veto es un PAR, no un outfit — esa decisión es la que hace que
   la función sirva (ver el comentario de `pasaVetos` en lib/estilo.js).
   Por eso la fila muestra las dos prendas y no una combinación entera.

   ⚠️  UNA PRENDA BORRADA SE MUESTRA IGUAL, y no es un descuido.
       El borrado de prendas es blando, así que un veto puede seguir
       apuntando a algo que ya no está en el armario. Esconderlo dejaría
       una fila que no se puede leer —"algo con algo"— y que solo se
       podría borrar a ciegas. Se dice cuál es y que está borrada.

   Y hay tres estados, no dos, en si la prenda existe:

       nombre + activa true    la prenda está
       nombre + activa false   está borrada, se dice
       nombre null             no se pudo leer (falló la consulta)

   El tercero NO es "borrada": es "no sé". Decirle borrada a algo que
   no se pudo consultar sería afirmar un hecho que nadie averiguó.
   ══════════════════════════════════════════════════════════════════ */
/* ══════════════════════════════════════════════════════════════════
   LA VENTANA — UN MODAL ARRIBA DEL MODAL DEL PERFIL

   ⚠️  DOS OVERLAYS ABIERTOS A LA VEZ TIENEN TRES COSAS QUE COORDINAR, Y
       LAS TRES FALLAN EN SILENCIO SI NO SE HACEN.

   1. ESCAPE. El perfil escucha `keydown` en `window` (ver App.js) y el
      Ayuda también. Si esta ventana solo agregara otro oyente, una
      tecla Escape cerraría LAS DOS: volverías al armario en vez de
      volver al perfil.

      Se resuelve escuchando en fase de CAPTURA y cortando ahí. Para un
      evento que va a parar a `window`, la captura corre ANTES que el
      burbujeo, así que este oyente ve el Escape primero, cierra lo suyo
      y `stopPropagation()` impide que el del perfil se entere. Es
      comportamiento definido por el DOM, no un truco de orden de
      registro — que sí sería frágil.

   2. EL CLIC AFUERA. El fondo del perfil cierra el perfil al clic. Sin
      `stopPropagation` en el clic de ESTA caja, tocar adentro de la
      ventana de vetos cerraría el perfil por debajo.

   3. EL SCROLL DEL FONDO. Los dos ponen `overflow: hidden` y los dos
      restauran lo que encontraron. Como esta se desmonta primero,
      restaura "hidden" —el que había puesto el perfil— y el perfil
      restaura el original al cerrarse. Anidan bien porque cada uno
      guarda lo que encontró, no un valor fijo.

   La capa la da `.modal-sobre-modal`, con el mismo criterio que
   `.modal-sobre-visor`: una clase, no un selector que dependa del
   orden del DOM.
   ══════════════════════════════════════════════════════════════════ */
function VentanaVetos({ vetos, onCambio, alCambiar, onCerrar }) {
  // Ver src/foco.js (BA-14).
  const cajaVetos = useFocoAtrapado();
  useEffect(() => {
    const previo = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const alTeclear = (e) => {
      if (e.key !== "Escape") return;
      // Ver el punto 1 de arriba: corta acá para que el perfil no se
      // cierre también.
      e.stopPropagation();
      onCerrar();
    };
    window.addEventListener("keydown", alTeclear, true);
    return () => {
      document.body.style.overflow = previo;
      window.removeEventListener("keydown", alTeclear, true);
    };
  }, [onCerrar]);

  return (
    <div
      className="modal modal-sobre-modal"
      role="dialog"
      aria-modal="true"
      aria-label={t("listas.vetosTitulo", "Combinaciones que marcaste como que no van")}
      onClick={onCerrar}
    >
      <div className="modal-caja" ref={cajaVetos} onClick={(e) => e.stopPropagation()}>
        <div className="modal-cabecera">
          <h2 className="modal-titulo">
            {t("listas.vetosTitulo", "Combinaciones que marcaste como que no van")}
          </h2>
          <button
            className="modal-cerrar"
            onClick={onCerrar}
            aria-label={t("comun.cerrar", "Cerrar")}
          >
            ×
          </button>
        </div>
        <div className="modal-cuerpo">
          <Vetos vetos={vetos} onCambio={onCambio} alCambiar={alCambiar} />
        </div>
      </div>
    </div>
  );
}

function Vetos({ vetos, onCambio, alCambiar }) {
  const [yendo, setYendo] = useState(null);
  const [error, setError] = useState("");

  // La clave de una fila es el PAR, que es lo que la identifica. Los
  // ids vienen ya ordenados por el servidor (`prenda_a < prenda_b`).
  const clave = (v) => `${v.prenda_a}|${v.prenda_b}`;

  async function quitar(v) {
    setYendo(clave(v));
    setError("");
    try {
      await api.borrarVeto(v.prenda_a, v.prenda_b);
      await onCambio();
      alCambiar?.();
    } catch (e) {
      setError(tError(e));
    } finally {
      setYendo(null);
    }
  }

  const nombreDe = (nombre, activa) => {
    if (nombre == null) {
      return <em className="tenue">{t("listas.prendaDesconocida", "Prenda que ya no está")}</em>;
    }
    return (
      <>
        {nombre}
        {activa === false && (
          <span className="tenue texto-mini">
            {" "}
            {t("listas.prendaBorrada", "(borrada)")}
          </span>
        )}
      </>
    );
  };

  return (
    <div className="mis-listas-grupo">
      {/* Sin título acá: lo pone la cabecera de la ventana. Repetirlo
          sería el mismo texto dos veces con dos centímetros de por
          medio. */}
      {error && <div className="mensaje-error texto-mini" role="alert">{error}</div>}

      {vetos.map((v) => (
        <div key={clave(v)} className="mis-listas-fila">
          <div className="mis-listas-nombre">
            <span>
              <strong>{nombreDe(v.nombre_a, v.activa_a)}</strong>
              <span className="tenue"> + </span>
              <strong>{nombreDe(v.nombre_b, v.activa_b)}</strong>
              {/* El motivo ya está traducido en los doce idiomas: es el
                  mismo catálogo que usa el desplegable de "No combina"
                  en Qué me pongo. Un veto viejo puede no tener motivo
                  —el campo es opcional— y entonces no se dibuja nada. */}
              {v.motivo && (
                <span className="tenue texto-mini">
                  {" · "}
                  {t(`opciones.motivoVeto.${v.motivo}`, v.motivo)}
                </span>
              )}
            </span>
          </div>

          <button
            className="boton-texto"
            onClick={() => quitar(v)}
            disabled={yendo === clave(v)}
            title={t("listas.borrarTitulo", "Borrar esta {que}", {
              que: t("listas.veto", "marca"),
            })}
          >
            {yendo === clave(v) ? "..." : t("listas.borrar", "Borrar")}
          </button>
        </div>
      ))}

      <p className="texto-mini tenue" style={{ margin: "4px 0 0" }}>
        {t("listas.vetoNota", "Al quitar una, esas dos prendas vuelven a poder aparecer juntas en las sugerencias.")}
      </p>
    </div>
  );
}
